import isEmpty from 'lodash/isEmpty';
import each from 'lodash/each';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import classNames from 'classnames';
import uuidV4 from 'uuid/v4';
import {compose} from 'redux';
import Alert from '@spothero/ui/alert';
import Button from '@spothero/ui/button';
import Checkbox from '@spothero/ui/checkbox';
import Form from '@spothero/ui/form';
import FormElementError from '@spothero/ui/form-element-error';
import FormGroup from '@spothero/ui/form-group';
import FormGroupError from '@spothero/ui/form-group-error';
import Label from '@spothero/ui/label';
import TextButton from '@spothero/ui/text-button';
import TextInput from '@spothero/ui/text-input';
import withConfig from 'config/withConfig';
import LicensePlateAPI from 'api/license-plate';
import {notify} from 'notification/notification-actions';
import UserUtils from 'user/user-utils';
import SegmentUtils from 'utils/segment';

class AddNewLicensePlateForm extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        user: PropTypes.object,
        currentPlates: PropTypes.array,
        formErrors: PropTypes.array,
        showTitle: PropTypes.bool,
        showCTAs: PropTypes.bool,
        showTowAlert: PropTypes.bool,
        allowNaming: PropTypes.bool,
        fromAccount: PropTypes.bool,
        onPlateNumberChanged: PropTypes.func,
        onPlateSaveSuccess: PropTypes.func,
        onPlateSaveError: PropTypes.func,
        onNevermindClick: PropTypes.func,
        sendNotification: PropTypes.func.isRequired,
    };
    static defaultProps = {
        allowNaming: true,
    };
    state = {
        areActionsDisabled: false,
        formError: null,
        hasEnteredLicenseMobile: false,
    };

    _onLicenseInputChange = evt => {
        const {isMobile, onPlateNumberChanged} = this.props;
        const value = evt.target.value;

        if (isMobile) {
            // when on mobile we want to capitalize the plate number if a value is present
            // otherwise set it back to normal case so the placeholder isn't all caps
            this.setState({
                hasEnteredLicenseMobile: !isEmpty(value),
            });
        }

        if (onPlateNumberChanged) {
            onPlateNumberChanged(value);
        }
    };

    _onAddLicensePlateClick = evt => {
        evt.preventDefault();

        this._submitForm(this.submit());
    };

    async _submitForm(formData) {
        const {
            user,
            fromAccount,
            currentPlates,
            onPlateSaveSuccess,
            onPlateSaveError,
            sendNotification,
        } = this.props;
        const {plateName, plateNumber, saveLicensePlateAsDefault} = formData;
        let isNewPlate = true;

        each(currentPlates, item => {
            if (plateNumber.toUpperCase() === item.plate.toUpperCase()) {
                isNewPlate = false;
            }
        });

        if (isNewPlate) {
            this.setState({
                areActionsDisabled: true,
                formError: null,
            });

            try {
                const {
                    data: {data},
                } = await LicensePlateAPI.add({
                    userId: user.id,
                    data: {
                        license_plate: plateNumber, // eslint-disable-line camelcase
                        name: plateName,
                        is_default: saveLicensePlateAsDefault, // eslint-disable-line camelcase
                    },
                });

                this.setState({
                    areActionsDisabled: false,
                });

                SegmentUtils.track({
                    event: 'Account Step Completed',
                    properties: {
                        'license plate updated': true,
                        'from screen': fromAccount ? 'account' : 'checkout',
                    },
                });

                onPlateSaveSuccess({
                    id: data.license_plate_id,
                    name: data.name,
                    plate: data.license_plate,
                    isDefault: data.is_default,
                });
            } catch (error) {
                if (error.data && error.data.data) {
                    error.data = error.data.data;
                }

                const errors = error.data.errors;

                this.setState({
                    areActionsDisabled: false,
                    formError: errors[0].messages, // TODO: test this to see output
                });

                SegmentUtils.track({
                    event: 'Error Message Displayed',
                    properties: {
                        'error message': `Oops! We couldn't save that license plate.`,
                        'error screen': fromAccount ? 'account' : 'checkout',
                        'error action': 'updating license plate',
                    },
                });

                if (onPlateSaveError) {
                    onPlateSaveError(errors);
                }
            }
        } else {
            sendNotification({
                id: uuidV4(),
                type: 'error',
                message: 'That license plate is already in your account.',
            });
        }
    }

    submit() {
        const data = this._newLicensePlate.submit();

        if (data && data.saveLicensePlateAsDefault) {
            data.saveLicensePlateAsDefault = Boolean(
                data.saveLicensePlateAsDefault
            );
        }

        return data;
    }

    render() {
        const {
            isMobile,
            user,
            showTitle,
            showCTAs,
            formErrors,
            onNevermindClick,
            allowNaming,
            showTowAlert,
        } = this.props;
        const {
            areActionsDisabled,
            formError,
            hasEnteredLicenseMobile,
        } = this.state;
        const numberClasses = classNames('LicensePlate-number', {
            'LicensePlate-number-has-entry': hasEnteredLicenseMobile,
        });
        const errors = formErrors ? formErrors : formError;

        return (
            <div className="AddNewLicensePlateForm">
                {showTitle && <h2 className="subtitle">Add a License Plate</h2>}
                <Form
                    ref={node => {
                        this._newLicensePlate = node;
                    }}
                    id="AddNewLicensePlateForm-form"
                >
                    {showTowAlert && (
                        <Alert type="highlight">
                            Please confirm so you don&apos;t get towed.
                        </Alert>
                    )}
                    <FormGroup inline={allowNaming ? !isMobile : false}>
                        <TextInput
                            required
                            name="plateNumber"
                            className={numberClasses}
                            label={<Label>License Plate</Label>}
                            placeholder={isMobile ? 'Enter License Plate' : ''}
                            error={
                                <FormElementError>
                                    Please enter your plate number.
                                </FormElementError>
                            }
                            maxLength={18}
                            onChange={this._onLicenseInputChange}
                        />
                        {allowNaming && (
                            <TextInput
                                name="plateName"
                                label={
                                    <Label>License Plate Name (optional)</Label>
                                }
                                placeholder={
                                    isMobile
                                        ? 'License Plate Name (optional)'
                                        : ''
                                }
                                maxLength={30}
                            />
                        )}
                    </FormGroup>
                    {user.status === UserUtils.AUTH_STATE.USER && (
                        <FormGroup>
                            <Checkbox
                                id="AddNewLicensePlateForm-save-license-default"
                                name="saveLicensePlateAsDefault"
                                label={
                                    <Label>
                                        Make this my default license plate
                                    </Label>
                                }
                            />
                        </FormGroup>
                    )}
                    {errors && <FormGroupError>{errors}</FormGroupError>}
                </Form>
                {showCTAs && (
                    <div className="FormActions">
                        <Button
                            color="primary"
                            disabled={areActionsDisabled}
                            loading={areActionsDisabled}
                            onClick={this._onAddLicensePlateClick}
                        >
                            Add Plate
                        </Button>
                        <TextButton
                            disabled={areActionsDisabled}
                            onClick={onNevermindClick}
                        >
                            Nevermind
                        </TextButton>
                    </div>
                )}
            </div>
        );
    }
}

const mapDispatchToProps = {
    sendNotification: notify,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(
        null,
        mapDispatchToProps,
        null,
        {forwardRef: true}
    )
);

export default enhance(AddNewLicensePlateForm);
